import { create } from 'zustand'
import type { ShowcaseConfiguration } from '@/types/theme'
import { getHomeBodyImageFromConfig, getMemberNavStyle, toCssBackgroundImage } from '@/lib/theme-helpers'
import { getContrastText, resetCSSVariables } from './theme-css-utils'

// ============================================
// BLOQUEIO DE ACESSO
// ============================================
/**
 * Área bloqueada para o aluno por qualquer um dos dois motivos:
 * - `member_area_blocked`: bloqueio manual de moderação (escopo: rotas de membro);
 * - `owner_plan_expired`: dono sem plano Mentorfy ativo (escopo: área inteira).
 */
export function isThemeAccessBlocked(
  theme: ShowcaseConfiguration | null | undefined,
): boolean {
  return theme?.member_area_blocked === true || theme?.owner_plan_expired === true
}

// ============================================
// CONFIGURAÇÃO (deve ser igual ao theme-init.js)
// ============================================
const CONFIG = {
  VERSION: '3.2.0',
  FRESH_TTL: 1 * 60 * 1000,      // 1 minuto
  STALE_TTL: 24 * 60 * 60 * 1000, // 24 horas
  FETCH_TIMEOUT: 10000,           // 10 segundos (mais tolerante no React)
  STORAGE_PREFIX: 'mentorfy_theme_v3_',
  PREVIEW_COOKIE: 'mfy_theme_preview' // gravado pelo proxy quando a URL tem ?preview=1
}

// ============================================
// MODO PREVIEW (botão "Visualizar" do Brand Studio)
// ============================================
/**
 * Em preview o tema NÃO passa pelo localStorage e é buscado em
 * `/api/themes/[domain]/preview` (sem cache). Lê a query (primeiro acesso) e o
 * cookie de sessão gravado pelo proxy (sobrevive aos redirects e ao login).
 */
export function isThemePreviewMode(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('preview')
    if (fromQuery === '1') return true
    if (fromQuery === '0') return false
    return document.cookie
      .split(';')
      .some((part) => part.trim() === `${CONFIG.PREVIEW_COOKIE}=1`)
  } catch {
    return false
  }
}

const themeApiPath = (domain: string) =>
  `/api/themes/${domain}${isThemePreviewMode() ? '/preview' : ''}`

// ============================================
// TIPOS
// ============================================
interface CacheData {
  version: string
  theme: ShowcaseConfiguration
  timestamp: number
}

type CacheStatus = 'fresh' | 'stale' | 'expired'

interface ThemeState {
  // State
  currentTheme: ShowcaseConfiguration | null
  domain: string | null
  isLoading: boolean
  isInitialized: boolean
  isBlockStatusVerified: boolean
  error: string | null

  // Actions
  initialize: (domain: string) => Promise<void>
  fetchTheme: (domain: string, force?: boolean) => Promise<void>
  checkAndRevalidate: (domain: string) => void
  applyTheme: (config: ShowcaseConfiguration) => void
  resetTheme: () => void
}

// ============================================
// HELPERS DE CACHE
// ============================================
const getStorageKey = (domain: string) => CONFIG.STORAGE_PREFIX + domain

const getCache = (domain: string): CacheData | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = localStorage.getItem(getStorageKey(domain))
    if (!raw) return null

    const data = JSON.parse(raw) as CacheData
    if (!data || data.version !== CONFIG.VERSION) {
      localStorage.removeItem(getStorageKey(domain))
      return null
    }

    return data
  } catch {
    return null
  }
}

const setCache = (domain: string, theme: ShowcaseConfiguration): boolean => {
  if (typeof window === 'undefined') return false

  try {
    localStorage.setItem(getStorageKey(domain), JSON.stringify({
      version: CONFIG.VERSION,
      theme,
      timestamp: Date.now()
    }))
    return true
  } catch {
    return false
  }
}

const getCacheStatus = (cache: CacheData | null): CacheStatus => {
  if (!cache || !cache.timestamp) return 'expired'

  const age = Date.now() - cache.timestamp

  if (age < CONFIG.FRESH_TTL) return 'fresh'
  if (age < CONFIG.STALE_TTL) return 'stale'
  return 'expired'
}

// ============================================
// APLICAR CSS VARIABLES
// ============================================
const applyCSSVariables = (config: ShowcaseConfiguration) => {
  if (typeof window === 'undefined') return

  const root = document.documentElement

  const deriveToolCardBorder = (baseColor: string): string => {
    const contrast = getContrastText(baseColor)
    if (contrast === '#0b0b0c') {
      return `color-mix(in srgb, ${baseColor} 82%, black 18%)`
    }
    if (contrast === '#ffffff') {
      return `color-mix(in srgb, ${baseColor} 82%, white 18%)`
    }
    return `color-mix(in srgb, ${baseColor} 82%, white 18%)`
  }

  const deriveToolCardBorderHover = (baseColor: string): string => {
    const contrast = getContrastText(baseColor)
    if (contrast === '#0b0b0c') {
      return `color-mix(in srgb, ${baseColor} 78%, black 22%)`
    }
    if (contrast === '#ffffff') {
      return `color-mix(in srgb, ${baseColor} 80%, white 20%)`
    }
    return `color-mix(in srgb, ${baseColor} 80%, white 20%)`
  }

  const setProperty = (property: string, value: string | undefined) => {
    if (value) {
      root.style.setProperty(property, value)
    }
  }

  // === DESIGN SYSTEM VARIABLES ===
  if (config.login) {
    const primaryColor = config.login.components?.form?.button?.background
    if (primaryColor) {
      root.style.setProperty('--brand-primary', primaryColor)
      root.style.setProperty('--primary', primaryColor)
      root.style.setProperty('--ring', primaryColor)
      root.style.setProperty('--login-form-input-ring', primaryColor)
    }
    setProperty('--primary-foreground', config.login.components?.form?.button?.text)

    setProperty('--secondary', config.login.layout?.form?.footer?.background || config.login.components?.form?.input?.background)
    setProperty('--secondary-foreground', config.login.layout?.form?.footer?.text || config.login.components?.form?.input?.text)
    setProperty('--accent', config.login.components?.form?.input?.background)
    setProperty('--accent-foreground', config.login.components?.form?.input?.text)

    setProperty('--background', config.home?.layout?.body?.color || config.login.layout?.body?.color)
    setProperty('--foreground', config.login.components?.form?.input?.text)

    setProperty('--card', config.login.layout?.form?.background)
    setProperty('--card-foreground', config.login.components?.form?.input?.text)
    setProperty('--popover', config.login.layout?.form?.background)
    setProperty('--popover-foreground', config.login.components?.form?.input?.text)

    setProperty('--input', config.login.components?.form?.input?.background)
    setProperty('--border', config.login.layout?.form?.border || config.login.components?.form?.input?.background)

    setProperty('--muted', config.login.layout?.form?.footer?.background)
    setProperty('--muted-foreground', config.login.layout?.form?.footer?.text)

    // Login Specific
    setProperty('--login-header-bg', config.login.layout?.header?.background)
    setProperty('--login-header-text', config.login.layout?.header?.text)
    setProperty('--login-body-bg', config.login.layout?.body?.color)
    setProperty('--login-body-title', config.login.layout?.body?.title)
    setProperty('--login-body-description', config.login.layout?.body?.description)
    setProperty('--login-body-background-image', config.login.layout?.body?.image)
    setProperty('--login-body-background-fill', config.login.layout?.body?.imageFill)
    setProperty('--login-body-background-position', config.login.layout?.body?.imagePosition)
    setProperty('--login-form-bg', config.login.layout?.form?.background)
    setProperty('--login-form-border', config.login.layout?.form?.border)
    setProperty('--login-form-text', config.login.components?.typography?.title)
    setProperty('--login-form-forgot', config.login.components?.typography?.forgotPassword)
    setProperty('--login-form-input-text', config.login.components?.form?.input?.text)
    setProperty('--login-form-input-bg', config.login.components?.form?.input?.background)
    setProperty('--login-form-button-bg', config.login.components?.form?.button?.background)
    setProperty('--login-form-button-text', config.login.components?.form?.button?.text)
    setProperty('--login-form-footer-bg', config.login.layout?.form?.footer?.background)
    setProperty('--login-form-footer-text', config.login.layout?.form?.footer?.text)
    setProperty('--login-form-register', config.login.layout?.form?.footer?.registerLink)
  }

  // === HOME PAGE ===
  if (config.home) {
    const headerBg = config.home.layout?.header?.background
    if (headerBg) {
      root.style.setProperty('--header-bg', headerBg)
    }
    setProperty('--header-text', config.home.layout?.header?.text)

    setProperty('--body-bg', config.home.layout?.body?.color)
    setProperty(
      '--body-bg-image',
      toCssBackgroundImage(getHomeBodyImageFromConfig(config)),
    )
    setProperty('--body-bg-fill', config.home.layout?.body?.imageFill)
    setProperty('--body-bg-position', config.home.layout?.body?.imagePosition)
    setProperty('--body-title', config.home.layout?.body?.title)
    setProperty('--body-text', config.home.layout?.body?.text)
    setProperty('--body-text-secondary', config.home.layout?.body?.textSecondary)
    setProperty('--body-featured', config.home.layout?.body?.featuredColor)
    setProperty('--body-warning', config.home.layout?.body?.warning)
    setProperty('--body-danger', config.home.layout?.body?.danger)

    // Se body.text estiver definido, atualiza --foreground para consistência global
    if (config.home.layout?.body?.text) {
      root.style.setProperty('--foreground', config.home.layout.body.text)
    }

    setProperty('--hero-title', config.home.components?.banner?.title)
    setProperty('--hero-description', config.home.components?.banner?.description)
    setProperty('--hero-button-bg', config.home.components?.banner?.button?.background)
    setProperty('--hero-button-text', config.home.components?.banner?.button?.text)

    setProperty('--footer-bg', config.home.layout?.footer?.background)
    setProperty('--footer-text', config.home.layout?.footer?.text)

    const featuredColor = config.home.layout?.body?.featuredColor
    if (featuredColor) {
      root.style.setProperty('--accent-base', featuredColor)
      root.style.setProperty('--brand-secondary', featuredColor)
    }
  }

  // === MENTORING PAGE ===
  if (config.mentoring) {
    setProperty('--mentoring-featured', config.mentoring.layout?.body?.featuredColor)
    setProperty('--progress-color', config.mentoring.layout?.body?.progressColor)
    setProperty('--warning-color', config.mentoring.layout?.body?.warningColor)
    setProperty('--mentoring-content-bg', config.mentoring.layout?.body?.contentBackground)
    setProperty('--mentoring-neutral-bg', config.mentoring.layout?.body?.neutralBackground)
    setProperty('--mentoring-sidebar-bg', config.mentoring.layout?.body?.sidebarBackground)
    setProperty('--mentoring-module-bg', config.mentoring.layout?.body?.moduleBackground)
    setProperty('--mentoring-module-text', config.mentoring.layout?.body?.moduleText)
    setProperty('--mentoring-content-text', config.mentoring.layout?.body?.contentText)
  }

  // === LEARN TOOLS (derivado) ===
  const toolBaseBg =
    config.mentoring?.layout?.body?.neutralBackground ||
    config.mentoring?.layout?.body?.contentBackground ||
    config.home?.layout?.body?.color

  if (toolBaseBg) {
    setProperty('--tool-card-bg', toolBaseBg)
    setProperty('--tool-card-hover', `color-mix(in srgb, ${toolBaseBg} 88%, white 12%)`)
    setProperty('--tool-card-border', deriveToolCardBorder(toolBaseBg))
    setProperty('--tool-card-border-hover', deriveToolCardBorderHover(toolBaseBg))
    setProperty('--tool-input-bg', `color-mix(in srgb, ${toolBaseBg} 82%, white 18%)`)
  }

  // === LESSON PAGE (derivado) ===
  // Light or dark variants follow the luminance of the mentor's body background, using the same
  // fallback chain as --background above; the surfaces (--lesson-surface*, --lesson-accent*) are
  // derived in globals.css from these. An unparseable colour resets everything to the CSS defaults.
  const lessonVars = [
    '--lesson-contrast',
    '--lesson-text',
    '--lesson-text-2',
    '--lesson-text-3',
    '--lesson-line',
  ]
  const lessonContrast = getContrastText(
    config.home?.layout?.body?.color || config.login?.layout?.body?.color,
  )
  if (lessonContrast) {
    const lessonLight = lessonContrast === '#0b0b0c'
    setProperty('--lesson-contrast', lessonLight ? '#000000' : '#ffffff')
    setProperty('--lesson-text', lessonLight ? '#18181b' : '#f3f3f5')
    setProperty('--lesson-text-2', lessonLight ? '#52525b' : '#b9b9c2')
    setProperty('--lesson-text-3', lessonLight ? '#71717a' : '#8c8c97')
    setProperty('--lesson-line', lessonLight ? 'rgb(0 0 0 / 0.12)' : 'rgb(255 255 255 / 0.1)')
    if (lessonLight) root.setAttribute('data-lesson-scheme', 'light')
    else root.removeAttribute('data-lesson-scheme')
  } else {
    lessonVars.forEach((name) => root.style.removeProperty(name))
    root.removeAttribute('data-lesson-scheme')
  }
  // "Done" colour: only when the mentor picked one (otherwise the CSS default green applies)
  if (config.mentoring?.layout?.body?.progressColor) {
    setProperty('--lesson-ok', config.mentoring.layout.body.progressColor)
  } else {
    root.style.removeProperty('--lesson-ok')
  }
  // With a body background image the main column gets its own surface so text never sits on the picture
  if (toCssBackgroundImage(getHomeBodyImageFromConfig(config))) {
    root.style.setProperty('--lesson-main-bg', 'var(--lesson-surface)')
    root.style.setProperty('--lesson-main-pad', '20px')
  } else {
    root.style.removeProperty('--lesson-main-bg')
    root.style.removeProperty('--lesson-main-pad')
  }

  if (config.mentoring?.layout?.body?.progressColor) {
    setProperty('--tool-progress-fill-start', config.mentoring.layout.body.progressColor)
    setProperty('--tool-progress-fill-end', config.mentoring.layout.body.progressColor)
    setProperty('--tool-progress-success-start', config.mentoring.layout.body.progressColor)
    setProperty('--tool-progress-success-end', config.mentoring.layout.body.progressColor)
    setProperty('--tool-btn-success-bg', config.mentoring.layout.body.progressColor)
    setProperty('--tool-btn-success-hover', config.mentoring.layout.body.progressColor)
    setProperty('--tool-success-text', config.mentoring.layout.body.progressColor)
    setProperty('--tool-success-icon', config.mentoring.layout.body.progressColor)
    setProperty('--tool-gradient-success-start', config.mentoring.layout.body.progressColor)
    setProperty('--tool-gradient-success-end', config.mentoring.layout.body.progressColor)
  } else if (config.home?.layout?.body?.featuredColor) {
    setProperty('--tool-progress-fill-start', config.home.layout.body.featuredColor)
    setProperty('--tool-progress-fill-end', config.home.layout.body.featuredColor)
  }

  if (config.home?.layout?.body?.featuredColor) {
    setProperty('--tool-btn-primary-bg', config.home.layout.body.featuredColor)
    setProperty('--tool-btn-primary-hover', config.home.layout.body.featuredColor)
    setProperty('--tool-selected-border', config.home.layout.body.featuredColor)
    setProperty('--tool-gradient-primary-start', config.home.layout.body.featuredColor)
    setProperty('--tool-gradient-primary-end', config.home.layout.body.featuredColor)
  } else if (config.mentoring?.layout?.body?.featuredColor) {
    setProperty('--tool-btn-primary-bg', config.mentoring.layout.body.featuredColor)
    setProperty('--tool-btn-primary-hover', config.mentoring.layout.body.featuredColor)
    setProperty('--tool-selected-border', config.mentoring.layout.body.featuredColor)
  }

  const toolPrimaryBg =
    config.home?.layout?.body?.featuredColor ||
    config.mentoring?.layout?.body?.featuredColor ||
    config.login?.components?.form?.button?.background

  const toolPrimaryText =
    getContrastText(toolPrimaryBg) ||
    config.login?.components?.form?.button?.text ||
    undefined

  setProperty('--tool-btn-primary-text', toolPrimaryText)

  if (config.mentoring?.layout?.body?.warningColor) {
    setProperty('--tool-warning-text', config.mentoring.layout.body.warningColor)
    setProperty('--tool-warning-icon', config.mentoring.layout.body.warningColor)
  } else if (config.home?.layout?.body?.warning) {
    setProperty('--tool-warning-text', config.home.layout.body.warning)
    setProperty('--tool-warning-icon', config.home.layout.body.warning)
  }

  if (config.home?.layout?.body?.danger) {
    setProperty('--tool-error-text', config.home.layout.body.danger)
    setProperty('--tool-error-icon', config.home.layout.body.danger)
  }

  if (config.home?.layout?.body?.title) {
    setProperty('--tool-text-primary', config.home.layout.body.title)
  }

  const toolTextSecondary =
    config.mentoring?.layout?.body?.contentText ||
    config.home?.layout?.body?.textSecondary ||
    config.home?.layout?.body?.text

  if (toolTextSecondary) {
    setProperty('--tool-text-secondary', toolTextSecondary)
  }

  if (config.home?.layout?.body?.featuredSecundaryColor) {
    setProperty('--tool-info-text', config.home.layout.body.featuredSecundaryColor)
    setProperty('--tool-info-icon', config.home.layout.body.featuredSecundaryColor)
  }

  if (config.title) {
    document.title = config.title
  }

  // Estilo de navegação: o CSS (globals.css) liga os offsets do menu lateral por
  // este atributo; o theme-init.js grava o mesmo valor a partir do cache antes do React.
  root.setAttribute('data-nav-style', getMemberNavStyle(config))

  // Mark as white-label
  root.setAttribute('data-white-label', 'true')
  root.setAttribute('data-theme-applied', 'true')
}

// ============================================
// STORE
// ============================================
export const useThemeStore = create<ThemeState>()((set, get) => ({
  currentTheme: null,
  domain: null,
  isLoading: false,
  isInitialized: false,
  isBlockStatusVerified: false,
  error: null,

  initialize: async (domain: string) => {
    const { isInitialized } = get()
    if (isInitialized) return

    // Verificar se theme-init.js já aplicou o tema
    const themeAlreadyApplied = typeof window !== 'undefined' &&
      document.documentElement.hasAttribute('data-theme-applied')

    if (isThemePreviewMode()) {
      // PREVIEW: pula a hidratação por localStorage e busca direto na rota sem
      // cache. Fetch "em primeiro plano" (isLoading ligado) de propósito: sem
      // currentTheme os componentes cairiam nos assets padrão Mentorfy até a
      // resposta chegar, e um flash da marca errada é justamente o que o
      // botão "Visualizar" não pode mostrar.
      set({ isInitialized: true, domain, isBlockStatusVerified: false })
      await get().fetchTheme(domain, false)
      return
    }

    // Ler do cache
    const cache = getCache(domain)
    const status = getCacheStatus(cache)

    if (cache?.theme && (status === 'fresh' || status === 'stale')) {
      set({
        currentTheme: cache.theme,
        domain,
        isInitialized: true,
        isBlockStatusVerified: isThemeAccessBlocked(cache.theme)
      })

      // Se theme-init não aplicou, aplicar agora
      if (!themeAlreadyApplied) {
        applyCSSVariables(cache.theme)
      }

      // Sempre revalidar após hidratar por cache para reduzir janela
      // de inconsistência logo após salvar configuração no brand-studio.
      get().fetchTheme(domain, true)
    } else {
      // Sem cache válido, fazer fetch
      set({ isInitialized: true, isBlockStatusVerified: false })
      await get().fetchTheme(domain)
    }

    // Escutar eventos de atualização do theme-init.js
    if (typeof window !== 'undefined') {
      window.addEventListener('theme-updated', ((event: CustomEvent) => {
        if (event.detail?.domain === domain && event.detail?.theme) {
          set({
            currentTheme: event.detail.theme,
            isBlockStatusVerified: true
          })
        }
      }) as EventListener)
    }
  },

  fetchTheme: async (domain: string, isBackgroundRevalidation = false) => {
    const { isLoading, currentTheme } = get()

    // Não iniciar novo fetch se já está carregando
    if (isLoading) return

    // Em revalidação background, não mostrar loading
    if (!isBackgroundRevalidation) {
      set({ isLoading: true, error: null })
    }

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.FETCH_TIMEOUT)

      const response = await fetch(themeApiPath(domain), {
        signal: controller.signal
      })
      clearTimeout(timeoutId)

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const result = await response.json()

      if (result.success && result.data) {
        const theme = result.data as ShowcaseConfiguration

        // Salvar no cache (nunca em preview: o tema sem cache não pode virar
        // o cache "antigo" de 24h da próxima visita normal)
        if (!isThemePreviewMode()) {
          setCache(domain, theme)
        }

        // Atualizar estado
        set({
          currentTheme: theme,
          domain,
          isLoading: false,
          isBlockStatusVerified: true
        })

        // Aplicar CSS variables
        applyCSSVariables(theme)
      } else {
        throw new Error('Invalid response')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch theme'
      const isTimeout = error instanceof Error && error.name === 'AbortError'

      // Em revalidação background, não mostrar erro se já tem tema
      if (isBackgroundRevalidation && currentTheme) {
        set({
          isLoading: false,
          isBlockStatusVerified: get().isBlockStatusVerified || isThemeAccessBlocked(currentTheme)
        })
        return
      }

      set({
        error: isTimeout ? 'Theme loading timed out' : errorMessage,
        isLoading: false,
        isBlockStatusVerified: false
      })
    }
  },

  checkAndRevalidate: (domain: string) => {
    const { isLoading } = get()

    // Não revalidar se já está carregando
    if (isLoading) return

    // Verificar status do cache
    const cache = getCache(domain)
    const status = getCacheStatus(cache)

    // Sempre revalidar em background para refletir alterações recentes
    // sem depender da janela de "fresh TTL" do cache local.
    console.log(`🔄 [Theme] Revalidating in background (status: ${status})`)
    get().fetchTheme(domain, true)
  },

  applyTheme: (config: ShowcaseConfiguration) => {
    applyCSSVariables(config)
    set({ currentTheme: config })
  },

  resetTheme: () => {
    resetCSSVariables()
    set({
      currentTheme: null,
      domain: null,
      error: null,
      isBlockStatusVerified: false
    })
  }
}))
